// 시트 보기의 순수 모델 — EditorState 를 행·열 좌표로 펼치고, 범위 복사·붙여넣기를 액션으로 바꾼다.

import { isBlankItem } from "../../build-rows";
import { safeCalcItem } from "../../safe-calc";
import type { VatMode } from "../../../money/vat";
import {
  CellParse,
  CellParseContext,
  parseCell,
  RowField,
  rowFromCells,
} from "../paste";
import { itemMarginOf, marginEditable, SET_LOCKED_FIELDS, stateAreaM2 } from "../reducer";
import {
  CellPatch,
  EditorAction,
  EditorItem,
  EditorItemPatch,
  EditorState,
  EditorTrade,
  FillField,
  FilledItemInput,
  ItemRef,
} from "../types";

/* ── 열 ─────────────────────────────────────────────────────── */

// 시트 칸이 쓰는 필드 — 붙여넣기·가져오기의 RowField 그대로(원가 포함).
export type SheetField = RowField;

export type SheetColumnKey = SheetField | "amount" | "margin";

export interface SheetColumn {
  key: SheetColumnKey;
  label: string;
  /** 읽기전용(계산) 열이면 null. */
  field: SheetField | null;
  numeric: boolean;
}

const col = (key: SheetColumnKey, label: string, numeric = false): SheetColumn => ({
  key,
  label,
  field: key === "amount" || key === "margin" ? null : key,
  numeric,
});

const DETAIL_COLUMNS: SheetColumn[] = [
  col("space_code", "공간"),
  col("scope", "부위"),
  col("item", "작업내역"),
  col("unit", "단위"),
  col("qty", "수량", true),
  col("material_unit_price", "자재단가", true),
  col("labor_unit_price", "노무단가", true),
  col("amount", "금액", true),
  col("note", "비고"),
];

const SIMPLE_COLUMNS: SheetColumn[] = [
  col("space_code", "공간"),
  col("scope", "부위"),
  col("item", "작업내역"),
  col("unit", "단위"),
  col("qty", "수량", true),
  col("simple_price", "단가", true),
  col("amount", "금액", true),
  col("note", "비고"),
];

// 원가·마진 보기 — 금액 열 바로 뒤에 원가·마진 두 열.
const withCost = (columns: SheetColumn[]): SheetColumn[] => {
  const at = columns.findIndex((column) => column.key === "amount") + 1;
  return [...columns.slice(0, at), col("cost_unit_price", "원가", true), col("margin", "마진%", true), ...columns.slice(at)];
};

const DETAIL_COST_COLUMNS = withCost(DETAIL_COLUMNS);
const SIMPLE_COST_COLUMNS = withCost(SIMPLE_COLUMNS);

/** 간단 모드는 단가가 한 열(자재+노무 합)이다. */
export const sheetColumns = (simple: boolean, showCost = false): SheetColumn[] =>
  showCost ? (simple ? SIMPLE_COST_COLUMNS : DETAIL_COST_COLUMNS) : simple ? SIMPLE_COLUMNS : DETAIL_COLUMNS;

// 마진 비율(0.25) → 칸 글자 「25」 · 소수 첫째 자리까지.
export const marginPctText = (margin: number): string => String(Number((margin * 100).toFixed(1)));

// 마진(원가 대비) → 이익률(판매가 대비) 글자 「20」 · m/(1+m) · −100% 이하는 셀 수 없어 빈 글자.
export const profitRatePctText = (margin: number): string =>
  margin <= -1 ? "" : String(Number(((margin / (1 + margin)) * 100).toFixed(1)));

// 시트 칸 읽기 — 원가 칸(빈 칸 = null)까지 붙여넣기와 한 벌(`parseCell`).
export const parseSheetCell = (
  field: SheetField,
  raw: string,
  ctx: CellParseContext,
  current?: { labor_unit_price: number; unit?: string }
): CellParse => parseCell(field, raw, ctx, current);

/* ── 행 ─────────────────────────────────────────────────────── */

export type SheetRow =
  | { kind: "trade"; tradeKey: string; trade: EditorTrade }
  | { kind: "item"; ref: ItemRef; item: EditorItem }
  | { kind: "child"; ref: ItemRef; item: EditorItem };

export type SheetDataRow = Extract<SheetRow, { kind: "item" | "child" }>;

export const isDataRow = (row: SheetRow | undefined): row is SheetDataRow =>
  row !== undefined && row.kind !== "trade";

/** 화면 행의 키 — 머리 줄은 `t:공종`, 항목·자식은 `i:항목`. */
export const sheetRowKey = (row: SheetRow): string =>
  row.kind === "trade" ? `t:${row.tradeKey}` : `i:${row.item.key}`;

const tradeKeyOf = (row: SheetRow): string =>
  row.kind === "trade" ? row.tradeKey : row.ref.tradeKey;

/** 공종 머리 줄 → 항목 → 그 SET 자식 순으로 펼친다. 접힘은 화면이 거른다. */
export const sheetRowsOf = (state: EditorState): SheetRow[] => {
  const rows: SheetRow[] = [];
  for (const trade of state.trades) {
    rows.push({ kind: "trade", tradeKey: trade.key, trade });
    for (const item of trade.items) {
      rows.push({
        kind: "item",
        ref: { tradeKey: trade.key, itemKey: item.key, parentKey: null },
        item,
      });
      for (const child of item.children) {
        rows.push({
          kind: "child",
          ref: { tradeKey: trade.key, itemKey: child.key, parentKey: item.key },
          item: child,
        });
      }
    }
  }
  return rows;
};

const LOCKED = new Set<string>([...SET_LOCKED_FIELDS, "simple_price"]);

/** 머리 줄·계산 열은 못 고친다. SET 부모는 단위·수량·단가를 못 고친다. */
export const isCellEditable = (row: SheetRow, column: SheetColumn): boolean => {
  if (column.key === "margin") return row.kind !== "trade" && marginEditable(row.item);
  if (row.kind === "trade" || column.field === null) return false;
  if (row.item.children.length > 0 && LOCKED.has(column.field)) return false;
  return true;
};

/* ── 선택 범위 ──────────────────────────────────────────────── */

export interface CellPos {
  row: number;
  col: number;
}

/** 양 끝을 포함하는 사각형. */
export interface SheetRange {
  top: number;
  left: number;
  bottom: number;
  right: number;
}

const clamp = (value: number, max: number): number =>
  Math.max(0, Math.min(Math.floor(value), max));

/** 앵커·커서를 격자 안으로 자르고 위-왼쪽이 앞이 되게 정렬한다. 격자가 비면 null. */
export const normalizeRange = (
  anchor: CellPos,
  cursor: CellPos,
  size: { rows: number; cols: number }
): SheetRange | null => {
  if (size.rows <= 0 || size.cols <= 0) return null;
  const r1 = clamp(anchor.row, size.rows - 1);
  const r2 = clamp(cursor.row, size.rows - 1);
  const c1 = clamp(anchor.col, size.cols - 1);
  const c2 = clamp(cursor.col, size.cols - 1);
  return {
    top: Math.min(r1, r2),
    bottom: Math.max(r1, r2),
    left: Math.min(c1, c2),
    right: Math.max(c1, c2),
  };
};

/** 범위 안의 항목 행(머리 줄 제외). `children: false` 면 SET 자식도 뺀다 — 삭제·삽입용. */
export const dataRowsIn = (
  rows: SheetRow[],
  range: SheetRange,
  opts: { children?: boolean } = {}
): SheetDataRow[] => {
  const withChildren = opts.children ?? true;
  return rows
    .slice(range.top, range.bottom + 1)
    .filter(isDataRow)
    .filter((row) => withChildren || row.kind === "item");
};

export const itemRefsIn = (
  rows: SheetRow[],
  range: SheetRange,
  opts: { children?: boolean } = {}
): ItemRef[] => dataRowsIn(rows, range, opts).map((row) => row.ref);

/* ── 칸 값 · TSV 내보내기 ────────────────────────────────────── */

export interface SheetTextOptions {
  /** 주면 공간을 이름으로 내보낸다. */
  spaces?: { code: string; label: string }[];
  /** 수량식 원문을 내보낼지(앱 안 복사용). 기본은 계산값 — 엑셀이 `=평*3` 을 못 읽는다. */
  exprs?: boolean;
  // 마진 칸 계산 기준 — 없으면 포함.
  vatMode?: VatMode;
}

/** 칸의 글자. 숫자는 쉼표 없이 준다 — 엑셀이 숫자로 읽게. SET 부모의 잠긴 칸은 빈 칸. */
export const cellText = (
  row: SheetRow,
  column: SheetColumn,
  opts: SheetTextOptions = {}
): string => {
  if (row.kind === "trade") return column.key === "item" ? row.trade.name : "";
  const item = row.item;
  const isSet = item.children.length > 0;
  switch (column.key) {
    case "space_code":
      return opts.spaces?.find((s) => s.code === item.space_code)?.label ?? item.space_code;
    case "scope":
    case "item":
    case "unit":
    case "note":
      return item[column.key];
    case "qty":
      if (isSet) return "";
      return opts.exprs && item.qty_expr ? item.qty_expr : String(item.qty);
    case "material_unit_price":
    case "labor_unit_price":
      return isSet ? "" : String(item[column.key]);
    case "simple_price":
      return isSet ? "" : String(item.material_unit_price + item.labor_unit_price);
    case "amount":
      return String(safeCalcItem(item).amount);
    case "cost_unit_price":
      return isSet || item.cost_unit_price === null || item.cost_unit_price === undefined ? "" : String(item.cost_unit_price);
    case "margin": {
      const margin = itemMarginOf(item, opts.vatMode ?? "INCL");
      return margin === null ? "" : marginPctText(margin);
    }
    default:
      return "";
  }
};

const needsQuote = /[\t\n\r"]/;

/** 칸 배열을 TSV 로. 탭·개행·따옴표가 든 칸은 엑셀처럼 따옴표로 감싼다. */
export const toTsv = (cells: string[][]): string =>
  cells
    .map((line) =>
      line
        .map((cell) => (needsQuote.test(cell) ? `"${cell.replace(/"/g, '""')}"` : cell))
        .join("\t")
    )
    .join("\n");

/** 범위를 TSV 로. 공종 머리 줄은 빼고 항목 행만 — 엑셀에 붙이면 표가 그대로 선다. */
export const rangeToTsv = (
  rows: SheetRow[],
  columns: SheetColumn[],
  range: SheetRange,
  opts: SheetTextOptions = {}
): string => {
  const cols = columns.slice(range.left, range.right + 1);
  const lines = dataRowsIn(rows, range).map((row) =>
    cols.map((column) => cellText(row, column, opts))
  );
  return toTsv(lines);
};

// 자식이 전부 빈 줄인 SET 부모는 저장(toDraftItem)처럼 자기 칸을 가진 한 줄로 본다.
const asSavedRow = (row: SheetDataRow): SheetDataRow =>
  row.item.children.length > 0 && row.item.children.every(isBlankItem)
    ? { ...row, item: { ...row.item, children: [] } }
    : row;

// 잠긴 견적의 편집을 새 버전 시트로 옮기는 TSV — 상세 열 고정 · SET 부모와 빈 줄(저장이 버리는 줄)은 빼고 자식은 일반 항목으로(총액 같음) · 계산 열은 빈 칸(붙일 때 「고칠 수 없는 칸」 이 안 뜨게).
export const sheetToTsv = (state: EditorState, opts: SheetTextOptions = {}): string => {
  const rows = sheetRowsOf(state)
    .filter(isDataRow)
    .map(asSavedRow)
    .filter((row) => row.item.children.length === 0 && !isBlankItem(row.item));
  return toTsv(
    rows.map((row) => DETAIL_COLUMNS.map((column) => (column.field === null ? "" : cellText(row, column, opts))))
  );
};

/** TSV 를 칸 배열로. 따옴표로 시작한 칸은 엑셀 규칙("" → ")으로 풀고, 끝의 개행 하나는 버린다. */
export const parseTsv = (raw: string): string[][] => {
  const text = raw.replace(/\r\n?/g, "\n");
  if (text === "") return [];
  const lines: string[][] = [];
  let line: string[] = [];
  let i = 0;
  while (i <= text.length) {
    let cell = "";
    if (text[i] === '"') {
      let j = i + 1;
      let closed = false;
      while (j < text.length) {
        if (text[j] === '"') {
          if (text[j + 1] === '"') {
            cell += '"';
            j += 2;
            continue;
          }
          closed = true;
          j += 1;
          break;
        }
        cell += text[j];
        j += 1;
      }
      if (closed) {
        i = j;
      } else {
        // 닫히지 않은 따옴표는 글자 그대로 읽는다.
        cell = "";
      }
    }
    while (i < text.length && text[i] !== "\t" && text[i] !== "\n") {
      cell += text[i];
      i += 1;
    }
    line.push(cell);
    const sep = text[i];
    i += 1;
    if (sep !== "\t") {
      lines.push(line);
      line = [];
      // 끝의 개행 하나는 빈 줄을 만들지 않는다.
      if (sep === "\n" && i === text.length) break;
    }
  }
  return lines;
};

/* ── 붙여넣기 ───────────────────────────────────────────────── */

export interface PasteOptions {
  spaces?: { code: string; label: string }[];
}

export interface SheetPastePlan {
  /** 보낼 액션. 바꿀 것이 없으면 null. 한 액션이라 되돌리기 한 단계다. */
  action: Extract<EditorAction, { type: "SET_CELLS" }> | null;
  /** 못 읽어 건너뛴 칸 수. */
  unclear: number;
  /** 읽기전용·격자 밖이라 버린 칸 수(빈 칸은 안 센다). */
  ignored: number;
  /** 붙인 뒤 고를 사각형(지금 행 번호 기준 — 새 행은 공종 끝 뒤에 이어진다). 바꿀 것이 없으면 null. */
  target: SheetRange | null;
  /** 새로 생긴 행이 시작되는 자리(지금 행 번호 기준). 새 행이 없으면 null. */
  freshFrom: number | null;
  /** 간단 보기에서 격자를 넘친 붙여넣기가 상세 보기면 다 들어가고 값이 다른 칸에 앉는다 — 단가 칸이 하나라 뒤 칸이 밀렸다. */
  needsDetail: boolean;
}

const mergePatch = (
  patches: Map<SheetDataRow, EditorItemPatch>,
  row: SheetDataRow,
  patch: EditorItemPatch
): void => {
  patches.set(row, { ...(patches.get(row) ?? {}), ...patch });
};

// 단가는 간단 보기에서 한 칸, 상세 보기에서 자재단가 칸부터 — 같은 자리로 본다.
const slotOf = (key: SheetColumnKey | undefined): SheetColumnKey | null =>
  key === undefined ? null : key === "simple_price" ? "material_unit_price" : key;

// 간단 보기에서 격자를 넘친 클립을 같은 시작 칸에 상세 보기로 붙이면 값 든 칸이 모두 들어가고, 그중 하나라도 다른 칸에 앉을 때만 참.
const pasteNeedsDetail = (columns: SheetColumn[], left: number, clip: string[][], clipW: number): boolean => {
  const start = columns[left];
  if (!start || !columns.some((column) => column.key === "simple_price")) return false;
  if (left + clipW <= columns.length) return false;
  const detailLeft = DETAIL_COLUMNS.findIndex((column) => column.key === slotOf(start.key));
  let moved = false;
  for (let c = 0; c < clipW; c += 1) {
    if (clip.every((line) => (line[c] ?? "").trim() === "")) continue;
    const detail = DETAIL_COLUMNS[detailLeft + c];
    if (!detail) return false;
    if (slotOf(columns[left + c]?.key) !== slotOf(detail.key)) moved = true;
  }
  return moved;
};

/**
 * TSV 를 범위에 붙이는 계획. 클립보드가 선택보다 작고 나눠 떨어지면 선택 안을 되풀이해 채우고,
 * 아니면 선택 왼쪽 위부터 그 공종 안에서 아래로 붙이며 행이 모자라면 공종 끝에 새 행을 만든다.
 */
export const planPaste = (
  state: EditorState,
  rows: SheetRow[],
  columns: SheetColumn[],
  range: SheetRange,
  raw: string,
  opts: PasteOptions = {}
): SheetPastePlan => {
  const clip = parseTsv(raw);
  const ctx: CellParseContext = {
    qtyExpr: true,
    area_pyeong: state.area_pyeong,
    area_m2: stateAreaM2(state),
    spaces: opts.spaces,
  };
  const empty: SheetPastePlan = {
    action: null,
    unclear: 0,
    ignored: 0,
    target: null,
    freshFrom: null,
    needsDetail: false,
  };
  if (clip.length === 0 || rows.length === 0 || !rows[range.top]) return empty;

  const clipH = clip.length;
  const clipW = Math.max(...clip.map((line) => line.length));
  const needsDetail = pasteNeedsDetail(columns, range.left, clip, clipW);
  const patches = new Map<SheetDataRow, EditorItemPatch>();
  const unitHints = new Map<SheetDataRow, string>();
  let unclear = 0;
  let ignored = 0;

  const putCell = (row: SheetDataRow, colIndex: number, text: string): void => {
    const column = columns[colIndex];
    if (!column || !isCellEditable(row, column) || column.field === null) {
      if (text.trim() !== "") ignored += 1;
      return;
    }
    const parsed = parseSheetCell(column.field, text, ctx, row.item);
    if (!parsed.ok) {
      unclear += 1;
    } else if (column.field === "qty" && parsed.patch.unit !== undefined) {
      // 수량 칸의 「3EA」 단위는 같은 붙여넣기의 단위 칸까지 본 뒤 비어 있을 때만 쓴다.
      const { unit, ...rest } = parsed.patch;
      mergePatch(patches, row, rest);
      unitHints.set(row, unit);
    } else {
      mergePatch(patches, row, parsed.patch);
    }
  };

  const selRows = dataRowsIn(rows, range);
  const selW = range.right - range.left + 1;
  const tile =
    selRows.length > 0 &&
    selRows.length % clipH === 0 &&
    selW % clipW === 0 &&
    (selRows.length > clipH || selW > clipW);

  let insert: { tradeKey: string; index: number; items: FilledItemInput[] } | undefined;
  const right = Math.min(range.left + clipW - 1, columns.length - 1);
  let target: SheetRange = { top: range.top, left: range.left, bottom: range.top, right };
  let freshFrom: number | null = null;

  if (tile) {
    target = range;
    selRows.forEach((row, r) => {
      for (let c = 0; c < selW; c += 1) {
        putCell(row, range.left + c, clip[r % clipH][c % clipW] ?? "");
      }
    });
  } else {
    const tradeKey = tradeKeyOf(rows[range.top]);
    const targets = rows
      .slice(range.top)
      .filter((row) => tradeKeyOf(row) === tradeKey)
      .filter(isDataRow);
    const fresh: FilledItemInput[] = [];
    // 새 줄은 가져오기와 같은 rowFromCells 로 만든다 — 원가 칸도 원가로 담는다(#194).
    const fields = Array.from({ length: clipW }, (_, c) => columns[range.left + c]?.field ?? null);

    clip.forEach((line, r) => {
      const target = targets[r];
      if (target) {
        for (let c = 0; c < clipW; c += 1) putCell(target, range.left + c, line[c] ?? "");
        return;
      }
      // 빈 줄로는 새 행을 만들지 않는다.
      if (line.every((cell) => cell.trim() === "")) return;
      const built = rowFromCells(line, fields, ctx);
      unclear += built.unclear;
      ignored += line.filter(
        (cell, c) => cell.trim() !== "" && fields[c] === null
      ).length;
      fresh.push(built.row);
    });

    const trade = state.trades.find((t) => t.key === tradeKey);
    if (fresh.length > 0 && trade) {
      insert = { tradeKey, index: trade.items.length, items: fresh };
    }
    const used = Math.min(clip.length, targets.length);
    let lastOfTrade = range.top;
    rows.forEach((row, i) => {
      if (tradeKeyOf(row) === tradeKey) lastOfTrade = i;
    });
    const top = used > 0 ? rows.indexOf(targets[0]) : lastOfTrade + 1;
    const bottom = insert ? lastOfTrade + insert.items.length : rows.indexOf(targets[used - 1]);
    if (insert) freshFrom = lastOfTrade + 1;
    target = { top, left: range.left, bottom: Math.max(top, bottom), right };
  }

  unitHints.forEach((unit, row) => {
    if ((patches.get(row)?.unit ?? row.item.unit).trim() === "") mergePatch(patches, row, { unit });
  });
  const cellPatches: CellPatch[] = Array.from(patches, ([row, patch]) => ({
    ref: row.ref,
    patch,
  }));
  if (cellPatches.length === 0 && !insert) {
    return { action: null, unclear, ignored, target: null, freshFrom: null, needsDetail };
  }
  return {
    action: { type: "SET_CELLS", patches: cellPatches, ...(insert ? { insert } : {}) },
    unclear,
    ignored,
    target,
    freshFrom,
    needsDetail,
  };
};

// 붙여넣기 뒤 경고 토스트 문구 — 건너뛴 칸이 없어도 간단 보기에서 칸이 밀렸으면 상세 보기를 권한다. 알릴 게 없으면 null.
export const pasteWarningOf = (
  plan: Pick<SheetPastePlan, "unclear" | "ignored" | "needsDetail">
): { title: string; description: string } | null => {
  const parts = [
    plan.unclear > 0 ? `못 읽은 칸 ${plan.unclear}개` : null,
    plan.ignored > 0 ? `고칠 수 없는 칸 ${plan.ignored}개` : null,
  ].filter((part): part is string => part !== null);
  const hint = plan.needsDetail
    ? "상세 보기로 바꾼 뒤 붙여넣으세요 — 간단 보기는 단가가 한 칸이라 노무단가·비고 칸이 밀립니다"
    : null;
  if (parts.length === 0 && hint === null) return null;
  return {
    title: parts.length > 0 ? "붙여넣지 못한 칸이 있습니다" : "칸이 밀렸습니다",
    description: [parts.length > 0 ? `${parts.join(" · ")} — 건너뛰었습니다` : null, hint]
      .filter((line): line is string => line !== null)
      .join("\n"),
  };
};

/* ── 되돌리기 뒤 커서 ──────────────────────────────────────── */

/** 두 화면 행 목록에서 처음 바뀐 칸(`next` 기준). 새 행·지운 행이면 col 은 null, 바뀐 칸이 없으면 null. */
export const firstChangedCell = (
  prev: SheetRow[],
  next: SheetRow[],
  columns: SheetColumn[]
): { row: number; col: number | null } | null => {
  const before = new Map(prev.map((row) => [sheetRowKey(row), row]));
  for (let i = 0; i < next.length; i += 1) {
    const row = next[i];
    if (!isDataRow(row)) continue;
    const old = before.get(sheetRowKey(row));
    if (!isDataRow(old)) return { row: i, col: null };
    if (old.item === row.item) continue;
    const col = columns.findIndex(
      (column) => cellText(old, column, { exprs: true }) !== cellText(row, column, { exprs: true })
    );
    if (col >= 0) return { row: i, col };
  }
  const after = new Set(next.map(sheetRowKey));
  const gone = prev.findIndex((row) => isDataRow(row) && !after.has(sheetRowKey(row)));
  return gone < 0 || next.length === 0 ? null : { row: Math.min(gone, next.length - 1), col: null };
};

/* ── 채우기 ─────────────────────────────────────────────────── */

const fillFieldsOf = (column: SheetColumn): FillField[] => {
  if (column.field === null) return [];
  if (column.field === "simple_price") return ["material_unit_price", "labor_unit_price"];
  return [column.field];
};

/** 범위 첫 항목 행의 값을 아래 행들에 복사하는 액션. 복사할 것이 없으면 null. */
export const fillDownAction = (
  rows: SheetRow[],
  columns: SheetColumn[],
  range: SheetRange
): Extract<EditorAction, { type: "FILL_DOWN" }> | null => {
  const refs = itemRefsIn(rows, range);
  const fields = columns.slice(range.left, range.right + 1).flatMap(fillFieldsOf);
  if (refs.length < 2 || fields.length === 0) return null;
  return { type: "FILL_DOWN", refs, fields };
};
